import { useEffect, useState } from 'react';
import { sanitizeValue } from '../../lib/sanitize';
// local
import { useFetch } from '../useFetch';
import { useOperator } from '../../contexts/OperatorContext';
import { useInventory } from '../useInventory';
import { useForm } from '../useForm';
import { BASE_URL_MAP } from '../../lib/environment';

type Action =
  | ''
  | 'addDevice'
  | 'createAccount'
  | 'disableMediafirst'
  | 'disableMediaroom'
  | 'verifyAccountCreation';

const creditOptions = [0, 100, 200, 300, 400, 500, 600, 700];

export const useCreateAccount = () => {
  const operator = useOperator();
  const {
    inventory: inv,
    getInventory,
    loading: inventoryLoading,
  } = useInventory(true);

  const optionMenus = {
    basePackages: (operator.packages as ResAccountSubscriberGroup[])
      .filter((p) => p.type === 'base' && p.active)
      .map((p) => ({
        id: p.id,
        name: p.description,
        value: p.id,
      })),
    creditLimit: creditOptions.map((option) => ({
      id: option,
      value: option,
      name: `$${option}`,
    })),
    experienceGroup: (operator.experienceGroups ?? [])
      .filter((eg) => eg.active)
      .map((eg) => ({ id: eg.id, name: eg.description, value: eg.id })),
    fipsCode: (operator.fips ?? [])
      .filter((f) => f.active)
      .map((fips) => ({ id: fips.code, name: fips.county, value: fips.code })),
    routingGroup: (operator.routingGroups ?? [])
      .filter((rg) => rg.active)
      .map((rg) => ({ id: rg.id, name: rg.description, value: rg.id })),
    transportTypeName: (operator.transports ?? [])
      .filter((t) => t.active)
      .map((transport) => ({
        id: transport.name,
        name: transport.name,
        value: transport.id,
      })),
  };

  const {
    active: formActive,
    values: formValues,
    onChange: onFormChange,
    onSubmit: onFormSubmit,
    resetForm,
    updateField,
    updateInitialState: updateFormState,
  } = useForm<CreateAccount>(
    {
      accountNumber: '',
      basePackage: '',
      creditLimit: operator.defaultCreditLimit ?? 0,
      devices: [],
      disableMediafirst: false,
      disableMediaroom: false,
      enhancedData: '',
      experienceGroup: operator.defaultExperienceGroupId,
      fipsCode: (optionMenus.fipsCode[0]?.id ?? '').toString(),
      ipAddress: '',
      loginId: '',
      phoneNumber: '',
      routingGroup: operator.defaultRoutingGroupId,
      subscriberGroupIDs: [],
      startIpAddress: undefined,
      timezoneName: operator.timeZone ?? '',
      transportTypeName: (
        optionMenus.transportTypeName[0]?.id ?? ''
      ).toString(),
    },
    () => {
      createAccount(formValues);
    }
  );

  const [action, setAction] = useState<Action>('');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [accountExists, setAccountExists] = useState(false);

  const [currentDevice, setCurrentDevice] =
    useState<CreateAccountDevice | null>(null);
  const [errors, setErrors] = useState<CreateAccountError[]>([]);
  const [devices, setDevices] = useState<CreateAccountDevice[]>([]);

  const [inventory, setInventory] = useState<InventoryDevice[]>([]);

  const [progress, setProgress] = useState<Progress>({
    current: 0,
    total: 10,
    message: 'Creating Account...',
  });
  const { error, loading, makeRequest, response } = useFetch();

  useEffect(() => {
    if (response && !loading && !error) {
      setProgress((progress) => ({
        ...progress,
        current: progress.current + 1,
      }));
      if (action === 'createAccount') verifyAccountCreation();
      if (action === 'verifyAccountCreation') setAccountExists(true);
      if (action === 'verifyAccountCreation' && accountExists) {
        setProgress((progress) => ({
          ...progress,
          message: `Account ${accountId} successfully created!`,
        }));

        if (formValues.disableMediafirst) disableMediafirst();
        if (formValues.disableMediaroom) disableMediaroom();
        if (devices.length) addDevice();
      }
      if (action === 'disableMediafirst' && accountExists) {
        setProgress((progress) => ({
          ...progress,
          message: `Successfully disabled MediaFirst`,
        }));

        if (formValues.disableMediaroom) disableMediaroom();
        if (devices.length) addDevice();
      }
      if (action === 'disableMediaroom' && accountExists) {
        setProgress((progress) => ({
          ...progress,
          message: `Successfully disabled Mediaroom`,
        }));

        if (formValues.disableMediafirst) disableMediafirst();
        if (devices.length) addDevice();
      }

      if (action === 'addDevice' && devices.length > 0 && accountExists)
        addDevice();
    }
    if (!loading && error) {
      if (action === 'addDevice')
        setErrors([
          ...errors,
          {
            description: currentDevice ? currentDevice.device.mac : '',
            name: currentDevice ? currentDevice.location : '',
            originalError: error.data,
            type: 'device',
          },
        ]);
      if (action === 'createAccount') {
        setProgress((progress) => ({
          ...progress,
          current: progress.total,
          message: 'Failed to create account',
        }));
        setErrors([
          ...errors,
          {
            description: 'Could not create account',
            name: (error.status ?? '').toString(),
            originalError: (
              error.data ?? 'Unable to create account'
            ).toString(),
            type: 'account',
          },
        ]);
      }
      if (action === 'disableMediaroom' || action === 'disableMediafirst') {
        setErrors([
          ...errors,
          {
            description: 'Unable to disable account',
            name: (error.status ?? '').toString(),
            originalError: (
              error.data ?? 'Unable to disable account'
            ).toString(),
            type: 'account',
          },
        ]);
      }
    }

    // eslint-disable-next-line
  }, [accountExists, error, loading, response]);

  useEffect(() => {
    setInventory(inv);
  }, [inv.length]);

  useEffect(() => {
    resetState();
    getInventory();
  }, [operator.id]);

  const addDevice = () => {
    const device: CreateAccountDevice = devices[0];

    setCurrentDevice(device);
    setDevices(devices.filter((d) => d.device.id !== device.device.id));
    setProgress((progress) => ({
      ...progress,
      current: progress.current + 1,
      message: `Adding ${device.device.modelName} ${device.device.mac}...`,
    }));

    if (accountId) {
      const deviceId =
        operator.type === 'Hospitality' ? device.device.id : device.device.mac;
      const body =
        operator.type === 'Hospitality'
          ? {
              LocationID: device.location,
              IsDVR: false,
            }
          : {
              Location: device.location,
              IsDVR: false,
            };

      makeRequest(
        `${BASE_URL_MAP[operator.type]}/${
          operator?.id
        }/Accounts/${accountId}/${deviceId}`,
        'post',
        body
      );
    }
  };

  const verifyAccountCreation = () => {
    setAction('verifyAccountCreation');
    if (accountId) {
      setProgress((progress) => ({
        ...progress,
        message: `Verifying account creation...`,
      }));

      makeRequest(
        `${BASE_URL_MAP['Residential']}/${operator.id}/Accounts/${accountId}`,
        'get'
      );
    }
  };

  const createAccount = (newAccount: CreateAccount) => {
    const mappedAccount: ApiResCreateAccount = mapResAccountForApi(
      newAccount,
      operator
    );
    setAccountId(newAccount.accountNumber);
    setAction('createAccount');
    if (newAccount.devices.length) setDevices(newAccount.devices);
    setProgress({
      current: 0,
      message: `Creating Account ${newAccount.accountNumber}...`,
      total:
        newAccount.devices.length * 2 +
        2 +
        (newAccount.disableMediafirst ? 2 : 0) +
        (newAccount.disableMediaroom ? 2 : 0),
    });

    makeRequest(
      `${BASE_URL_MAP['Residential']}/${operator.id}/Accounts`,
      'post',
      sanitizeValue(mappedAccount)
    );
  };

  const disableMediafirst = () => {
    if (accountId) {
      setAction('disableMediafirst');

      setProgress((progress) => ({
        ...progress,
        message: `Disabling MediaFirst...`,
      }));

      makeRequest(
        `${BASE_URL_MAP['Residential']}/${operator.id}/Accounts/${accountId}/Status/MediaFirst`,
        'put',
        {
          Enable: false,
        }
      );
    }
  };

  const disableMediaroom = () => {
    if (accountId) {
      setAction('disableMediaroom');

      setProgress((progress) => ({
        ...progress,
        message: `Disabling Mediaroom...`,
      }));

      makeRequest(
        `${BASE_URL_MAP['Residential']}/${operator.id}/Accounts/${accountId}/Status/MediaRoom`,
        'put',
        {
          Enable: false,
        }
      );
    }
  };

  const resetState = () => {
    resetForm();
    setProgress({ current: 0, total: 10, message: 'Creating Account...' });
    setAccountId(null);
    setDevices([]);
    setAction('');
    setErrors([]);
  };

  return {
    accountExists,
    createAccount,
    errors,
    formActive,
    formValues,
    inventory,
    inventoryLoading,
    loading,
    onFormChange,
    onFormSubmit,
    optionMenus,
    resetForm,
    resetState,
    progress,
    updateField,
    updateFormState,
  };
};

const mapResAccountForApi = (
  account: CreateAccount,
  operator: Operator
): ApiResCreateAccount => ({
  AccountNumber: account.accountNumber,
  CreditLimit: account.creditLimit,
  EnhancedData: account.enhancedData,
  ExperienceGroupID:
    operator.mediafirst && account.experienceGroup !== ''
      ? account.experienceGroup
      : undefined,
  FIPSCode: account.fipsCode,
  IPAddress:
    operator.mediafirst && account.ipAddress !== ''
      ? account.ipAddress
      : undefined,
  LoginID:
    operator.mediafirst && !operator.usesTgOAuth && account.loginId !== ''
      ? account.loginId
      : undefined,
  PhoneNumber:
    account.phoneNumber !== undefined
      ? parseInt(account.phoneNumber.replaceAll(/\D/g, ''))
      : undefined,
  RoutingGroupID:
    operator.mediafirst && account.routingGroup !== ''
      ? account.routingGroup
      : undefined,
  SubscriberGroupIDs: [account.basePackage, ...account.subscriberGroupIDs],
  StartIPAddress:
    operator.mediafirst && account.startIpAddress !== ''
      ? account.startIpAddress
      : undefined,
  TimeZoneName:
    account.timezoneName !== operator.timeZone
      ? account.timezoneName
      : undefined,
  TransportTypeName: account.transportTypeName,
});
